import React from 'react';
import { formatPhoneNumber } from 'Shared/utils/formatters/phoneNumber';

const MONO_STACK = "'gt-america-mono', 'Courier New', Courier, monospace";

/**
 * Returns a ReactNode representation of a blocked attribute value.
 * For monospace types (vin, drivers_license number), wraps the relevant
 * portion in a <span> with the canonical mono font stack.
 */
export const formatAttributeValue = (
  attributeType: string,
  value: string | null | undefined
): React.ReactNode => {
  if (!value) return null;

  switch (attributeType) {
    case 'email':
    case 'email_domain':
      return value;

    case 'phone_number':
      return formatPhoneNumber(value);

    case 'vin':
      return React.createElement(
        'span',
        { style: { fontFamily: MONO_STACK } },
        value
      );

    case 'drivers_license': {
      // Canonical format: "CA:B92-2210-4498" → "CA · B92-2210-4498" with monospace on number
      const colonIdx = value.indexOf(':');
      if (colonIdx === -1) return value;
      const state = value.slice(0, colonIdx);
      const number = value.slice(colonIdx + 1);
      return React.createElement(
        React.Fragment,
        null,
        `${state} · `,
        React.createElement(
          'span',
          { style: { fontFamily: MONO_STACK } },
          number
        )
      );
    }

    case 'name_dob': {
      // Canonical format: "jane|smith|1985-03-22" → "Jane Smith · 1985-03-22"
      const parts = value.split('|');
      if (parts.length < 3) return value;
      const [firstName, lastName, dob] = parts;
      const cap = (s: string) =>
        s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
      return `${cap(firstName)} ${cap(lastName)} · ${dob}`;
    }

    default:
      return value;
  }
};
